import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Card,
    Chip,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import { APIService, ArtButton, ArtDialog, ArtSelect, ArtSwitch, ArtTextField, confirmDialog, useApiService } from "@art/shared-ui";
import { useDispatch, useSelector } from "react-redux";
import { useIDPContext } from '../../../auth';
import { RootState, setUser } from "../../../store";
import { ART_URL } from "../../../Constant";
import { useAttributePermission, usePageWrapper } from "../../../hooks";
import ChangePasswordDialog from "./change-password-dialog";

const AccountsSettings = () => {
    const apiService = useApiService();
    const dispatch = useDispatch();
    const authState = useSelector((state: RootState) => state.auth);
    const { authGuardInstance } = useIDPContext();
    const tenantName = authState.tenantName;
    const canDeleteAccount = useAttributePermission(
        "ACCOUNT_SETTINGS", "PROFILE_MANAGEMENT", "DeleteMyAccount"
    );
    const accountEmail = authState.user?.email || "";
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [ownedWorkspaces, setOwnedWorkspaces] = useState<{ id: number; name: string }[]>([]);
    const [previewStatus, setPreviewStatus] = useState<"loading" | "ready" | "error">("loading");
    const accountReady = authState.isAuthenticated && !authState.loading &&
        Boolean(tenantName) && Boolean(accountEmail) && Boolean(authGuardInstance);
    const canConfirmDelete = canDeleteAccount && accountReady && previewStatus === "ready" &&
        confirmText === accountEmail && Boolean(deleteReason) && !isDeleting;

    const closeDeleteDialog = () => {
        if (isDeleting) return;
        setDeleteDialogOpen(false);
        setDeleteReason("");
        setConfirmText("");
    };

    const openDeleteDialog = async () => {
        if (!accountReady || !canDeleteAccount || !tenantName) return;
        setPreviewStatus("loading");
        setOwnedWorkspaces([]);
        setDeleteDialogOpen(true);
        try {
            const response = await idpService.get(`/tenants/${encodeURIComponent(tenantName)}/users/profile/account`);
            if (!Array.isArray(response.data?.workspaces)) throw new Error("Invalid workspace response");
            setOwnedWorkspaces(response.data.workspaces);
            setPreviewStatus("ready");
        } catch {
            setPreviewStatus("error");
        }
    };

    const handleDeleteAccount = async () => {
        if (!canConfirmDelete || !tenantName) return;
        setIsDeleting(true);
        try {
            await idpService.delete(`/tenants/${encodeURIComponent(tenantName)}/users/profile/account`, {
                data: { reason: deleteReason, confirm_email: confirmText },
            });
        } catch (error: any) {
            apiService.showSnack(error?.response?.data?.message || "Failed to delete account. Please try again.", "error");
            setIsDeleting(false);
            return;
        }
        setDeleteDialogOpen(false);
        apiService.showSnack("Account scheduled for deletion. You have 30 days to reactivate.", "success");
        authGuardInstance?.logout();
    };

    const [isMfaEnabled, setIsMfaEnabled] = useState(
        !!authState.user?.mfa_enabled
    );
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    const idpService = new APIService({
        ART_STORE: apiService.getStore(),
        BASE_URL: ART_URL + "/idp/api",
        DisableDefaultHeadder: true,
        DisableDefaultTenantUrl: true,
    });

    useEffect(() => {
        setIsMfaEnabled(!!authState.user?.mfa_enabled);
    }, [authState.user?.mfa_enabled]);

    const handleDisableMfa = async () => {
        if (!accountReady || !tenantName) return;
        try {
            const payload = {
                email: accountEmail,
                mfa_enabled: false,
            };

            const res = await idpService.put(
                `/tenants/${encodeURIComponent(tenantName)}/users/mfa-disable`,
                payload
            );

            dispatch(
                setUser({
                    ...authState.user,
                    mfa_enabled: false,
                })
            );
            setIsMfaEnabled(false);
            apiService.showSnack(
                res.data?.message || "Two-factor authentication disabled successfully",
                "success"
            );
        } catch (error) {
            console.error("Error disabling MFA:", error);
            apiService.showSnack("Failed to disable two-factor authentication", "error");
        }
    };

    const handleToggleMfa = async (value: boolean) => {
        if (!accountReady || !tenantName) return;
        if (value) {
            const mfaUrl = `${ART_URL}/idp/auth/tenant/users/enable-mfa?tenant=${encodeURIComponent(tenantName)}&redirect-uri=${ART_URL}/live/dashboard`;
            window.location.href = mfaUrl;
            return;
        }

        try {
            await confirmDialog(
                "Disable two-factor authentication?",
                "This will reduce account security for your workspace admins.",
                "/assets/images/disable.png",
                200
            );
            await handleDisableMfa();
        } catch {
        }
    };

    return (
        <>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1 }}>
                    Account settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage your sign-in security and account preferences.
                </Typography>
            </Box>

            <div className="row g-4">
                <div className="col-12">
                    <Stack spacing={3}>
                        <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: "none" }} data-qa="section-2fa">
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "flex-start", md: "center" },
                                    justifyContent: "space-between",
                                    gap: 2,
                                    flexDirection: { xs: "column", md: "row" },
                                }}
                            >
                                <Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        useFlexGap
                                        flexWrap="wrap"
                                        sx={{ mb: 1 }}
                                    >
                                        <Typography variant="h6">Two-factor authentication</Typography>
                                        <Chip
                                            size="small"
                                            color={isMfaEnabled ? "success" : "default"}
                                            label={isMfaEnabled ? "Enabled" : "Disabled"}
                                            variant="outlined"
                                        />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Add a second verification step to protect your account when you sign in.
                                    </Typography>
                                </Box>

                                <ArtSwitch
                                    label="Two-factor authentication"
                                    dataQa="mfa-toggle"
                                    required={false}
                                    error=""
                                    value={isMfaEnabled}
                                    onChange={handleToggleMfa}
                                />
                            </Box>

                            <Alert severity={isMfaEnabled ? "success" : "info"} sx={{ mt: 2, borderRadius: 2 }}>
                                {isMfaEnabled
                                    ? "Your account has an extra layer of sign-in protection."
                                    : "Recommended for all accounts, especially workspace owners and admins."}
                            </Alert>
                        </Card>

                        <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: "none" }} data-qa="section-password">
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Password
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Keep your account secure with a strong, unique password.
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: { xs: "flex-start", md: "center" },
                                    gap: 2,
                                    flexDirection: { xs: "column", md: "row" },
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Change password
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        You’ll need your current password to make this change.
                                    </Typography>
                                </Box>

                                <ArtButton
                                    label="Change Password"
                                    buttonType="button"
                                    onClick={() => setPasswordDialogOpen(true)}
                                />
                            </Box>
                            <Divider sx={{ my: 2.5 }} />
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", md: "center" }}
                                spacing={2}
                            >
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>Password recovery</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Forgot your password? Reset it using your email address.
                                    </Typography>
                                </Box>
                                <ArtButton
                                    label="Reset password"
                                    variant="outlined"
                                    buttonType="button"
                                    onClick={() => {
                                        window.location.href = `${ART_URL}/idp/auth/tenant/forgot-password?redirect-uri=${encodeURIComponent(ART_URL + "/")}`;
                                    }}
                                />
                            </Stack>
                        </Card>

                        {canDeleteAccount && (
                            <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, borderColor: "error.light", boxShadow: "none" }} data-qa="section-account-actions">
                                <Typography variant="h6" color="error.main" sx={{ mb: 1 }}>
                                    Delete account
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Delete your profile and the workspaces you own. Workspaces you joined
                                    by invitation will not be deleted, but your memberships will be removed.
                                </Typography>
                                <Box sx={{
                                    display: "flex", gap: 2,
                                    justifyContent: "space-between",
                                    alignItems: { xs: "flex-start", md: "center" },
                                    flexDirection: { xs: "column", md: "row" },
                                }}>
                                    <Box>
                                        <Chip label="30-day recovery window" size="small" variant="outlined" sx={{ mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Sign in within 30 days to choose whether to reuse your account or create fresh.
                                            {" "}Removed members must be invited again.
                                        </Typography>
                                    </Box>
                                    <ArtButton
                                        label="Delete Account"
                                        variant="outlined"
                                        buttonType="button"
                                        color="error"
                                        disabled={!accountReady}
                                        onClick={openDeleteDialog}
                                    />
                                </Box>
                            </Card>
                        )}
                    </Stack>
                </div>
            </div>

            <ChangePasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
            />

            <ArtDialog
                open={deleteDialogOpen && canDeleteAccount}
                onClose={closeDeleteDialog}
                title="Delete Account"
                size="medium"
                type="form"
                actionBuilder={() => (
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <ArtButton label="Cancel" variant="outlined" buttonType="button"
                            disabled={isDeleting} onClick={closeDeleteDialog} />
                        <ArtButton label={isDeleting ? "Deleting..." : "Delete Account"}
                            buttonType="button" color="error" disabled={!canConfirmDelete}
                            onClick={handleDeleteAccount} />
                    </Box>
                )}
            >
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Your profile and owned workspaces will be scheduled for deletion.
                </Typography>
                <Box sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Owned workspaces affected</Typography>
                    {previewStatus === "loading" && <Typography variant="body2">Loading workspaces…</Typography>}
                    {previewStatus === "error" && (
                        <Alert severity="error">Could not load workspaces. Close and reopen this dialog to retry.</Alert>
                    )}
                    {previewStatus === "ready" && (ownedWorkspaces.length ? (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {ownedWorkspaces.map(workspace => <Chip key={workspace.id} label={workspace.name} variant="outlined" />)}
                        </Stack>
                    ) : <Typography variant="body2">You do not own any workspaces.</Typography>)}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Invited workspaces stay intact. Memberships are permanently removed.
                    {" "}Sign in within 30 days to recover your profile and owned workspaces.
                    {" "}Choosing Create fresh gives up recovery.
                </Typography>
                <ArtSelect
                    label="Why are you deleting your account?"
                    value={deleteReason}
                    onChange={(event: any) => setDeleteReason(event.target.value)}
                    dataQa="delete-reason-select"
                    marginBottom={24}
                    options={[
                        "No longer need the service", "Found a better alternative",
                        "Too expensive", "Privacy concerns", "Difficult to use", "Other",
                    ].map(reason => ({ value: reason, label: reason }))}
                />
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Type <strong>{accountEmail}</strong> to confirm:
                </Typography>
                <ArtTextField
                    value={confirmText}
                    onChange={(event: any) => setConfirmText(event.target.value)}
                    placeholder={`Type "${accountEmail}" to confirm`}
                    dataQa="delete-confirm-text"
                />
            </ArtDialog>

        </>
    );
};

export default usePageWrapper(AccountsSettings, {
    page_title: "Accounts",
});
